import React, { useState, useEffect, useRef } from 'react'
import { Modal as ContentModal } from 'react-bootstrap'
import PropTypes from 'prop-types'
// import pdfjsLib from 'pdfjs-dist';
import { DndProvider } from 'react-dnd'
import { HTML5Backend } from 'react-dnd-html5-backend'

import Button from '../Button'
import DocumentPreviewHeaders from '../DocumentPreviewHeaders'
import { DocumentPreviewContainer } from '../DocumentPreviewContainer'
// import ToolTip from '../ToolTip'
import { useToggle } from '../utils/hooks'

const Spinner = () => (
  <div className='position-absolute top-50 start-50 translate-middle bg-transparent'>
    <div className='spinner-example text-primary' role='status' style={{
      marginLeft: '150px',
      marginBottom: '200px',
      height: '100px',
      width: '100px'
    }}>
      <span className='visually-hidden'>Loading...</span>
    </div>
  </div>
)

const DocumentPreviewModal = ({
  isOpen,
  onHide,
  file,
  loading,
  pdfjsLib
}) => {
  const pageRefs = useRef(null)
  const miniPageRefs = useRef(null)
  const [currentWidth, setCurrentWidth] = useState(100)
  const [currentOrientation, setCurrentOrientation] = useState(0)
  const [pageNumber, setPageNumber] = useState(0)
  const [totalPageNumbers, setTotalPageNumbers] = useState(0)
  const [isPDFJSLoading, setPDFJSLoading] = useState(true)
  const [isOriginalDocLandscape, setIsOriginalDocLandscape] = useState(false)
  const [leftNavbarOpen, setLeftNavbarOpen] = useToggle(true)
  const [setImage, handleSetImage] = useState('')
  const [draggableImageElement, setDraggableImageElements] = useState({ img: { top: 10, left: 10 } })

  useEffect(() => {
    if (isOpen) {
      setPageNumber(1)
      setCurrentOrientation(0)
      setDraggableImageElements({ img: { top: 10, left: 10 } })
      setLeftNavbarOpen(window.innerWidth >= 768)
      if (window.innerWidth >= 1400) setCurrentWidth(55)
      else if (window.innerWidth >= 1200) setCurrentWidth(65)
      else if (window.innerWidth >= 992) setCurrentWidth(75)
      else if (window.innerWidth >= 768) setCurrentWidth(85)
      else setCurrentWidth(100)

      const fileReader = new FileReader()
      switch (file.type) {
        case 'application/pdf':
          fileReader.onload = () => {
            const pdfTypeArray = new Uint8Array(fileReader.result)
            handlePdfLoad(pdfTypeArray)
          }
          fileReader.readAsArrayBuffer(file)
          break

        case 'image/png':
        case 'image/jpeg':
        case 'image/jpg':
        case 'image/gif':
          fileReader.onload = (e) => {
            handleImageLoad(e.target.result)
          }
          fileReader.readAsDataURL(file)
          break
        default:
          break // Maybe set an error message?
      }
    }
  }, [isOpen, file, setLeftNavbarOpen, handlePdfLoad])

  // UseEffect for resizing events. Helps enable automatic mobile-responsiveness with left navbar
  useEffect(() => {
    const resizeHandler = () => leftNavbarOpen && setLeftNavbarOpen(window.innerWidth >= 768)
    resizeHandler()
    window.addEventListener('resize', resizeHandler)
    return () => window.removeEventListener('resize', resizeHandler)
  }, [leftNavbarOpen, setLeftNavbarOpen])

  const handleImageLoad = (src) => {
    handleSetImage(src)
    const img = Object.assign(new Image(), { src, className: 'page', id: 'Test' })
    pageRefs?.current?.appendChild(img)
  }

  const handleCanvasClick = (pageNum) => {
    const element = document.getElementById(`canvasPage_${pageNum}`)
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' })
    }
  }

  const handlePdfLoad = (pdfTypeArray) => {
    const startTime = Date.now()

    pdfjsLib.getDocument(pdfTypeArray).promise.then(async(pdf) => {
      // Setting Pages
      const { numPages } = pdf
      console.log(`This PDF contains ${numPages} page(s).`)
      setTotalPageNumbers(numPages)

      // Intersection Observer API
      let previouslyIntersectingElement = null
      const handleIntersection = (entries) => {
        entries.forEach((entry) => {
          const visiblePageNumber = parseInt(entry.target.title.replace('Page ', ''), 10)
          const elementToModify = document.getElementById(`canvasPage+${visiblePageNumber}`)

          if (entry.isIntersecting) {
            // If a new element is intersecting, remove outline from the previous one
            if (previouslyIntersectingElement && previouslyIntersectingElement !== elementToModify) {
              previouslyIntersectingElement.style.outline = ''
            }

            // Calculating middle point of the element
            const elementRect = entry.boundingClientRect
            const middleY = elementRect.top + (elementRect.height / 2)

            // Check if the middle point is within the viewport
            const isMiddleInViewport = middleY >= 0 && middleY <= window.innerHeight

            // Border to the current intersecting element if the middle is in the viewport
            if (elementToModify && isMiddleInViewport) {
              previouslyIntersectingElement = elementToModify
              elementToModify.style.outline = '3px solid red'
            }
            // Update page number state
            setPageNumber(visiblePageNumber)
          } else {
            // If the entry is no longer intersecting, remove the CSS changes
            if (elementToModify) {
              elementToModify.style.outline = ''
            }
          }
        })
      }
      const observerOptions = {
        root: null,
        rootMargin: '0px',
        threshold: 0.5
      }

      const observer = new IntersectionObserver(handleIntersection, observerOptions)

      // Display loading spinner
      setPDFJSLoading(true)

      const pagePromises = []
      // Looping through the PDF
      for (let i = 0; i < numPages; i++) {
        const pageNum = i + 1
        const megabytes = file.size / (1024 * 1024)
        let viewScale

        // Setting viewScale of the PDF (quality)
        if (megabytes > 5) {
          console.log('mega file')
          viewScale = 1
        } else if (megabytes > 2.5) {
          console.log('medium file')
          viewScale = 1.25
        } else {
          console.log('small file')
          viewScale = 2.0
        }

        const promise = pdf.getPage(pageNum).then((page) => {
          const viewport = page.getViewport({ scale: viewScale })
          const isOriginalDocLandscape = viewport.width > viewport.height
          setIsOriginalDocLandscape(isOriginalDocLandscape)
          const marginWidth = isOriginalDocLandscape ? 'margin: 10px;' : 'margin: 10px;'
          const canvas = Object.assign(document.createElement('canvas'), {
            className: 'page',
            title: `Page ${pageNum}`,
            height: viewport.height,
            width: viewport.width,
            id: `canvasPage_${pageNum}`,
            style: marginWidth
          })
          const canvasContext = canvas.getContext('2d')
          return page.render({ canvasContext, viewport }).promise.then(() => {
            observer.observe(canvas) // Observe each canvas element
            return canvas // Return canvas element
          })
        })
        pagePromises.push(promise)
      }

      Promise.all(pagePromises)
        .then((canvases) => {
          setPDFJSLoading(false)
          // Sort canvases by page number before appending
          canvases.sort((a, b) => {
            const pageNumA = parseInt(a.id.replace('canvasPage_', ''), 10)
            const pageNumB = parseInt(b.id.replace('canvasPage_', ''), 10)
            return pageNumA - pageNumB
          })
          // Append canvases to the pageRefs in the correct order
          canvases.forEach((canvas) => {
            pageRefs?.current?.appendChild(canvas)
          })
        })
        .catch((error) => {
          setPDFJSLoading(false)
          console.error('Error loading PDF pages:', error)
        })

      for (let i = 0; i < numPages; i++) {
        const pageNum = i + 1
        pdf.getPage(pageNum).then((page) => {
          const viewport = page.getViewport({ scale: 0.25 })
          const canvas = Object.assign(document.createElement('canvas'), {
            className: 'pageMini pointer my-3 my-2',
            title: `Page ${pageNum}`,
            height: viewport.height,
            width: viewport.width,
            style: 'margin: 10px;',
            onclick: () => handleCanvasClick(pageNum),
            id: `canvasPage+${pageNum}`
          })
          const canvasContext = canvas.getContext('2d')
          page.render({ canvasContext, viewport }).promise.then()
          // page.getTextContent().then((text) => console.log(text));
          miniPageRefs?.current?.appendChild(canvas)
          const textSpan = Object.assign(document.createElement('span'), {
            textContent: `Page ${pageNum}`,
            className: 'text-center text-sm text-white my-1'
          })
          miniPageRefs?.current?.appendChild(textSpan)
        })
      }

      Promise.all(pagePromises).then(() => {
        const elapsedTime = Date.now() - startTime
        console.log(`Elapsed time: ${elapsedTime} milliseconds`)
        const additionalDiv = document.createElement('div')
        additionalDiv.textContent = 'End of Preview'
        additionalDiv.style.color = '#AEB5BC'
        pageRefs?.current?.appendChild(additionalDiv)
      })
    })
  }

  const toggleNavbar = () => {
    if (window.innerWidth >= 768) {
      // LEFT NAV BAR
      const sideNavbar = document.querySelector('.modal-preview-left-navbar')
      sideNavbar.style.left = leftNavbarOpen ? '-250px' : '0px'

      // MODAL CONTENT
      const modalBodyPages = document.getElementById('left-nav-pages')
      modalBodyPages.style.marginLeft = leftNavbarOpen ? '150px' : '0px'

      // Update the state correctly
      setLeftNavbarOpen((prevOpen) => !prevOpen)
    }
  }

  const pdfPreviewStyles = {
    width: `${currentWidth}%`,
    transition: 'width .5s',
    height: '100vh',
    marginLeft: leftNavbarOpen ? (window.innerWidth <= 768 ? '100px' : '150px') : '0px'
  }

  const miniPDFPreviewStyles = {
    height: '100%',
    transition: 'none',
    overflowY: 'auto',
    overflowX: 'hidden'
  }

  return (
    <ContentModal show={isOpen} size='xl' contentClassName='document-preview-modal w-100 overflow-hidden' dialogClassName='document-preview-modal-size' onHide={onHide} bsPrefix='p-0 modal'>
      {file?.type === 'application/pdf' || file?.type === 'image/jpeg' || file?.type === 'image/png' || file?.type === 'image/jpg' || file?.type === 'image/gif'
        ? <DocumentPreviewHeaders
          onHide={onHide}
          file={file}
          currentWidth={currentWidth}
          setCurrentWidth={setCurrentWidth}
          currentOrientation={currentOrientation}
          setCurrentOrientation={setCurrentOrientation}
          setDraggableImageElements={setDraggableImageElements}
          pageNumber={pageNumber}
          totalPageNumbers={totalPageNumbers}
          setPageNumber={setPageNumber}
          toggleNavbar={toggleNavbar}
          isOriginalDocLandscape={isOriginalDocLandscape}
          leftNavbarOpen={leftNavbarOpen}
        />
        // IF DOCUMENT IS NOT PREVIEWABLE
        : <ContentModal.Header closeButton={false} bsPrefix='modal-header d-flex align-items-center justify-content-between document-preview-header p-2 text-white'>
          <div className='col row d-flex align-items-center'>
            <div className='col-sm-12 col-md-6 d-flex align-items-center'>
              <span className='fas fa-file-exclamation mx-2' aria-hidden='true' />
              {file?.name && <span className='mx-2' aria-label={file.name}>{file.name}</span>}
            </div>
            <div className='col-sm-12 col-md-6 d-flex align-items-center justify-content-end'>
              <Button
                ghost
                ariaLabel='Close'
                className='p-1'
                onClick={() => onHide()}>
                {/* <ToolTip text='Close'>
                  <span className='fas fa-xmark text-white pointer' />
                </ToolTip> */}
              </Button>
            </div>
          </div>
        </ContentModal.Header>
      }

      {file &&
        <ContentModal.Body bsPrefix='modal-body d-flex flex-column align-items-center document-preview-modal-body p-0'>
          {file.type === 'application/pdf' &&
            <div className='modal-preview-left-navbar d-none d-md-flex position-absolute'>
              <div
                ref={miniPageRefs}
                id='left-nav-pages'
                className='document-preview-leftnav d-flex flex-column align-items-center p-3 p-lg-4 mini-page-preview'
              />
            </div>
          }
          {file.type === 'application/pdf' ? (
            <div className='w-100 d-flex justify-content-center document-preview-modal-body position-relative' style={{ overflowY: 'auto' }}>
              {/* PDF RENDER */}
              {(isPDFJSLoading || loading) && <Spinner />}
              <div ref={pageRefs} id='pages' className='d-flex flex-column align-items-center' style={pdfPreviewStyles} />
            </div>
          ) : (file.type === 'image/jpeg' || file.type === 'image/png' || file.type === 'image/jpg' || file.type === 'image/gif') ? (
            <DndProvider backend={HTML5Backend}>
              <DocumentPreviewContainer
                draggableImageElement={draggableImageElement}
                setDraggableImageElements={setDraggableImageElements}
                hideSourceOnDrag={true} // ? 99.9% sure we don't want to show the original spot. Can be converted to toggle.
                pageRefs={pageRefs}
                currentWidth={currentWidth}
                setImage={setImage}
                currentOrientation={currentOrientation}
              />
            </DndProvider>
          ) : (
            // IF NO PREVIEW AVAILABLE (NON-PDF OR IMAGE TYPE)
            <div className='d-flex justify-content-center align-items-center' style={{ height: '100vh' }}>
              <div className='alert alert-light fs-5 pt-3 px-3' role='alert' style={{ width: '600px' }}>
                <div className='d-flex align-items-center alert-heading mb-4'>
                  {<span className='fas fa-file-exclamation text-primary me-3' aria-hidden='true' />}
                  {<h3 className='text-dark mb-0'>Couldn&apos;t preview file.</h3>}
                </div>
                <p className='text-dark'>There was a problem displaying a preview.</p>
                <p className='text-dark'>Try downloading instead.</p>
                <Button block className='my-3' data-cy='return-to-login' onClick={() => {
                  const blob = new Blob([file], { type: file.type })
                  const url = URL.createObjectURL(blob)
                  const anchor = Object.assign(document.createElement('a'), { href: url, download: file.name }).click()
                  anchor.click()
                }}>
                  <span className='fas fa-download text-white pointer mx-2' />Download
                </Button>
              </div>
            </div>
          )}
        </ContentModal.Body>
      }
    </ContentModal>
  )
}

DocumentPreviewModal.propTypes = {
  onHide: PropTypes.func,
  isOpen: PropTypes.bool,
  file: PropTypes.object
}

export default DocumentPreviewModal
